/**
 * L'ORGANIZZAZIONE, IN UN ELENCO SOLO: le voci del menu **e** i passi del percorso.
 *
 * ## Perché un elenco e non due
 *
 * Un passo del percorso di configurazione è fatto di: un nome, un indirizzo, dei permessi, una
 * cosa da contare per sapere se è fatto, e ciò che deve venire prima. Una voce di menu è fatta
 * di: un nome, un indirizzo e dei permessi. Sono **la stessa lista**, mostrata due volte — e
 * scriverla due volte significa che fra due mesi dicono cose diverse.
 *
 * Non è un timore teorico: in questo repo è già successo tre volte fra menu desktop e cassetto
 * mobile, l'ultima con tredici voci di scarto. Da qui escono tutti e due, così una voce nuova
 * compare nel menu **e** nel percorso, o in nessuno dei due.
 *
 * ## Le tre voci di testa, e perché non sono passi
 *
 * `Holding`, `Dati dell'azienda` e `Regole di lavoro` aprono pagine, non soggetti da creare:
 * la prima è il gruppo, le altre due sono le due metà della vecchia scheda azienda (vedi
 * `companySections.ts`). Stanno nel menu, non nel percorso — tranne «Dati dell'azienda», che
 * del percorso è il primo passo perché un'anagrafica incompleta blocca la fatturazione.
 *
 * ## ⚠️ I nomi delle risorse sono verificati, non dedotti
 *
 * Ogni `resource` qui sotto è il tipo JSON:API che le pagine di quel soggetto usano davvero
 * (`erp-shifts`, `erp-sites`, `erp-roles`, …), letto dal codice che funziona. Un tipo
 * inventato non dà errore in compilazione e nemmeno a schermo: dà un conteggio che resta zero,
 * cioè un passo che risulta «da fare» per sempre, su una configurazione che c'è.
 */
import {
  IconAdjustments, IconBeach, IconBox, IconBuilding, IconBuildingSkyscraper,
  IconClockHour4, IconCreditCard, IconHierarchy2, IconIdBadge2, IconListCheck, IconMapPin,
  IconPlaneTilt, IconRoute, IconSitemap, IconUserShield, IconUsers,
} from '@tabler/icons-react';
import type { IconProps } from '@tabler/icons-react';
import { WORK_RULES_ENTRY_CLAIMS } from '@/features/tenants/companySections';
import type { ComponentType } from 'react';

export type SubjectIcon = ComponentType<IconProps>;

/** Il passo del percorso, per le voci che ne sono uno. */
export interface SubjectStep {
  /** L'ordine in cui si affrontano. Non è l'ordine del menu: è quello delle dipendenze. */
  order: number;
  /**
   * La risorsa da contare per sapere se il passo è fatto: una `GET` con `page.size: 1`, di cui
   * si legge solo `meta.total`. Assente = si misura in un altro modo (la riga dell'azienda) o
   * non si misura affatto, e allora si dichiara invece di indovinare.
   */
  resource?: string;
  /** I passi che devono essere fatti prima, o questo non si può nemmeno provare. */
  requires?: readonly string[];
  /** Cosa non funziona se lo si salta. Una riga, concreta: finisce nell'avviso. */
  why: string;
}

export interface OrganizationSubject {
  id: string;
  /** L'etichetta del menu. La stessa che il percorso mostra sotto il pallino. */
  label: string;
  to: string;
  icon: SubjectIcon;
  claim?: string;
  claimAny?: readonly string[];
  /** Il claim che spiega perché la voce non c'è, quando manca. */
  missingGate?: string;
  /** L'interruttore del modulo per azienda. */
  flag?: string;
  /** La voce esiste ma la pagina non ancora: si vede spenta, invece di portare nel vuoto. */
  future?: boolean;
  /** L'allarme sulla voce di menu: vedi `NavItem.alarmKey`. */
  alarmKey?: 'missingRotations' | 'hierarchyGaps' | 'employeeGaps' | 'sitesWithoutTimes' | 'groupsWithoutArea';
  step?: SubjectStep;
  /**
   * La sezione del menu, quando non è «Organizzazione». Dal 03-11-2026 Dipendenti e Gerarchia
   * stanno in «Risorse umane»: sono il lavoro di tutti i giorni dell'ufficio del personale, non la
   * configurazione dell'azienda. **Restano qui** perché sono passi del percorso — un'azienda nuova
   * passa comunque da loro — e l'elenco resta uno solo (regola 18): cambia solo dove si mostrano.
   */
  menuSection?: 'hr';
}

/**
 * ⚠️ **Le voci che sono diventate sezioni non sono più qui**, ed è il punto della
 * riorganizzazione del 23-09-2026: «Promemoria timbrature» e «Modalità di lavoro» erano voci
 * di menu **e** sezioni della scheda azienda, cioè due porte sulla stessa configurazione con
 * due nomi. Ora sono sezioni di «Regole di lavoro» e basta.
 */
export const ORGANIZATION_SUBJECTS: readonly OrganizationSubject[] = [
  {
    id: 'group',
    label: 'Aziende collegate',
    to: '/organization/company',
    icon: IconBuildingSkyscraper,
    claimAny: ['Tenant.update', 'canSeeAllTenants', 'canManageSubTenants'],
  },
  {
    id: 'company-data',
    label: 'Dati dell’azienda',
    to: '/organization/company-data',
    icon: IconBuilding,
    claimAny: ['Tenant.update', 'canManageSubTenants'],
    step: {
      order: 1,
      why: 'Senza partita IVA e sede legale non si emette un documento, e il primo che serve lo si scopre il giorno in cui serve.',
    },
  },
  {
    id: 'work-rules',
    label: 'Regole di lavoro',
    to: '/organization/work-rules',
    icon: IconListCheck,
    /* L'amministratore, e dall'08-10-2026 chi scrive una delle sue sezioni (l'ufficio del
       personale): vedi `SECTION_ALSO_FOR`. Dentro vede solo quelle. */
    claimAny: [...WORK_RULES_ENTRY_CLAIMS],
    step: {
      order: 10,
      requires: ['employees'],
      why: 'Sono le decisioni che valgono per tutti: chiusura del mese, tolleranze, pianificazione. Si prendono quando c’è chi le subisce.',
    },
  },

  {
    id: 'shifts',
    label: 'Turni di lavoro',
    to: '/organization/work-shifts',
    icon: IconClockHour4,
    claimAny: ['ErpEmployeeWorkingHour.update', 'ErpShift.update', 'ErpSiteWorkingTime.update'],
    /* Chi non ha una turnazione non ha turni in calendario: l'allarme lo vede chi può generarli. */
    alarmKey: 'missingRotations',
    step: {
      order: 2,
      resource: 'erp-shifts',
      why: 'Crearli prima delle sedi permette di assegnarli mentre si crea ogni sede, invece di tornarci dopo una per una.',
    },
  },
  {
    id: 'sites',
    label: 'Sedi',
    to: '/organization/sites',
    icon: IconMapPin,
    claim: 'ErpSite.update',
    /* Le sedi attive senza orari: chi ci lavora non ha pause né promemoria (`sitesAlarm.ts`). */
    alarmKey: 'sitesWithoutTimes',
    step: {
      order: 3,
      resource: 'erp-sites',
      requires: ['shifts'],
      why: 'Una sede senza orari operativi è una sede in cui nessuno riesce a timbrare, e l’errore che vede chi ci prova non dice questo.',
    },
  },
  {
    /* Dal 02-11-2026 qui, dopo Sedi, come in Lares: articoli, ubicazioni e lotti sono anagrafica
       dell'azienda. Resta sul claim e non su un interruttore: si vede solo dove è stato concesso. */
    id: 'warehouse',
    label: 'Magazzino',
    to: '/warehouse',
    icon: IconBox,
    claim: 'WarehouseStock.read',
  },
  {
    id: 'roles',
    label: 'Mansioni',
    to: '/organization/job-roles',
    icon: IconIdBadge2,
    /* Chi le scrive: l'amministratore dell'azienda e la direzione HR (BE v1.277.0, task 3581).
       La lettura no: ce l'ha anche il dipendente, perché serve a compilare i form. */
    claim: 'ErpRole.update',
    step: {
      order: 4,
      resource: 'erp-roles',
      why: 'La mansione dice che lavoro fa una persona, e turnazioni, abbonamenti e avvisi si possono legare a lei: averle prima permette di assegnarle mentre si creano i dipendenti.',
    },
  },
  {
    id: 'bu',
    label: 'Struttura BU',
    to: '/organization/business-units',
    icon: IconSitemap,
    /* Amministratore, direzione HR e responsabile operazioni (BE v1.277.0, task 3581). */
    claimAny: ['ErpBusinessGroup.update', 'ErpBusinessArea.update'],
    /* I gruppi senza area: chi ci sta dentro sparisce dai numeri per area (`buAlarm.ts`). */
    alarmKey: 'groupsWithoutArea',
    step: {
      order: 5,
      resource: 'erp-business-groups',
      why: 'Aree e gruppi sono il modo in cui i numeri si aggregano nei report: aggiungerli dopo vuol dire riclassificare lo storico.',
    },
  },
  {
    id: 'employees',
    label: 'Dipendenti',
    to: '/hr/employees',
    icon: IconUsers,
    claimAny: ['UserProfile.readAll', 'ErpEmployee.update'],
    /* Attivi senza sede e accessi mai usati: chi può sistemarli lo vede (`employeeAlarms.ts`). */
    alarmKey: 'employeeGaps',
    menuSection: 'hr',
    step: {
      order: 6,
      resource: 'erp-employees',
      requires: ['roles'],
      why: 'È il passo da cui dipendono tutti gli altri: senza persone, gerarchia e turnazioni non hanno a chi applicarsi.',
    },
  },
  {
    id: 'permissions',
    label: 'Utenti e ruoli',
    to: '/organization/permissions',
    icon: IconUserShield,
    /* «Utenti e ruoli» dal 23-10-2026: i ruoli di tutte le utenze dell'azienda, dipendenti ed
       esterni, accanto a «Dipendenti». La vede chi può assegnare ruoli — l'amministratore —
       col permesso che il sistema controlla davvero (`UserRole.create/delete`). */
    claimAny: ['UserRole.create', 'UserRole.delete'],
    /* Passo del percorso dal 02-11-2026: un'azienda creata dal Master nasce coi soli titolari, e
       sono loro a invitare gli altri. Non si misura: chi è stato invitato «abbastanza» non lo sa
       nessun conteggio — le utenze ci sono sempre, almeno quella del titolare. */
    step: {
      order: 7,
      requires: ['employees'],
      why: 'Chi non ha un’utenza non entra: non timbra, non chiede ferie, non rendiconta. Le utenze le crei qui, invitando le persone.',
    },
  },
  {
    id: 'hierarchy',
    label: 'Gerarchia',
    to: '/hr/hierarchy',
    icon: IconHierarchy2,
    claim: 'EmployeeSubordinate.update',
    /* Chi non ha un responsabile, o ce l'ha cessato: il sistema non sa a chi far approvare. */
    alarmKey: 'hierarchyGaps',
    menuSection: 'hr',
    step: {
      order: 8,
      resource: 'employee-subordinates',
      requires: ['employees'],
      why: 'Chi approva ferie e trasferte lo dice la gerarchia: senza, le richieste restano ferme e nessuno sa di doverle guardare.',
    },
  },
  {
    id: 'leave-types',
    /* Il nome della pagina, dal 07-10-2026: «Tipi di assenza» ne nominava un pezzo solo, e la
       pagina contiene anche le motivazioni e i periodi di blocco. */
    label: 'Configurazione assenze',
    to: '/organization/leave-config',
    icon: IconBeach,
    flag: 'leave.enabled',
    claim: 'LeaveType.update',
    step: {
      order: 9,
      resource: 'leave-types',
      why: 'Senza i tipi, la richiesta di ferie non ha niente da scegliere e il modulo si apre con una tendina vuota.',
    },
  },
  {
    id: 'reasons',
    label: 'Causali',
    /* Una pagina a sé dal 01-11-2026, come in Lares: era una sezione della rendicontazione. */
    to: '/organization/activity-reasons',
    icon: IconAdjustments,
    flag: 'reporting.enabled',
    claimAny: ['ActivityReason.update', 'TimesheetEntry.read'],
    step: {
      order: 11,
      resource: 'activity-reasons',
      why: 'Sono le voci con cui si rendiconta: senza, le ore si registrano ma non si sa **su cosa**.',
    },
  },
  /* «Stati protetti» non è più qui (01-11-2026): sono dati particolari del personale (104,
     maternità, cariche sindacali…), non una configurazione dell'azienda. Sta in «Risorse umane». */

  /* I soggetti che una pagina propria ce l'hanno (o l'avranno) e che **non** sono sezioni di
     «Regole di lavoro»: restano voci. Nessuno di loro blocca un altro, quindi nessuno è un passo. */
  /* ⚠️ Qui c'era «Blackout assenze», `/organization/leave-blackouts`, `future: true`: nel menu
     si vedeva spenta con scritto «in arrivo». Era falso — i periodi di blocco si scrivono da
     sempre in `/organization/leave-config`, la pagina «Configurazione assenze». Una
     voce «in arrivo» per una funzione che c'è manda a cercarla altrove e a non trovarla (07-10-2026).
     Non poteva puntare lì: le voci hanno l'indirizzo come chiave unica. Ci si arriva dalla voce
     «Configurazione assenze», e con `?tab=blackout` dal rimando in «Regole di lavoro → Assenze». */
  {
    id: 'travel-policies',
    label: 'Policy trasferte',
    to: '/organization/travel-policies',
    icon: IconPlaneTilt,
    flag: 'travel.enabled',
    /* Le policy le scrivono amministratore e direzione HR, le assegnazioni (le fasce) l'ufficio
       del personale (BE v1.277.0, task 3581): la pagina ha una scheda per ciascuna. */
    claimAny: ['TravelPolicy.update', 'TravelPolicyTier.update'],
  },
  {
    id: 'mileage-rates',
    label: 'Tariffario km',
    to: '/organization/mileage-rates',
    icon: IconRoute,
    flag: 'reporting.enabled',
    claim: 'ErpMileageRate.update',
  },
  {
    id: 'subscription-types',
    label: 'Tipi di abbonamento',
    to: '/organization/subscription-types',
    icon: IconCreditCard,
    flag: 'subscriptions.enabled',
    claim: 'ErpSubscriptionType.update',
  },
];

/** I passi del percorso, nell'ordine delle dipendenze. */
export const SETUP_STEPS = ORGANIZATION_SUBJECTS
  .filter((s): s is OrganizationSubject & { step: SubjectStep } => Boolean(s.step))
  .slice()
  .sort((a, b) => a.step.order - b.step.order);

/** Le risorse da contare, senza doppioni: una query per ciascuna. */
export const STEP_RESOURCES: readonly string[] = [
  ...new Set(SETUP_STEPS.map((s) => s.step.resource).filter((r): r is string => Boolean(r))),
];
